import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTheme } from "@mui/material";
import { Menu, MenuItem, Sidebar, menuClasses } from "react-pro-sidebar";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Icon } from "../../icons";
import { hexToRGBA } from "../../../utils/hex-to-rgba";
import { ossNavigationItems } from "../constants/navigation";
import "../../SideBar/SidebarStyles.css";

export const OssSidebar = ({ isMobile = false, toggled = false, onToggle, onNavigate }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { group_code, project_code } = useParams();
  const user = useSelector((state) => state.auth.user);
  const basePath = useMemo(
    () => `/oss/${group_code}/${project_code}`,
    [group_code, project_code]
  );

  const move = (path) => {
    navigate(path ? `${basePath}/${path}` : basePath);
    onNavigate?.();
    if (isMobile) {
      onToggle?.(false);
    }
  };

  const isActive = (path) => {
    const target = path.split("?")[0];
    const view = path.includes("?") ? new URLSearchParams(path.split("?")[1]).get("view") : "";

    if (!target) {
      return location.pathname === basePath;
    }

    if (view) {
      return location.pathname === `${basePath}/${target}` && searchParams.get("view") === view;
    }

    if (target === "orders") {
      return location.pathname.startsWith(`${basePath}/${target}`) && !searchParams.get("view");
    }

    return location.pathname === `${basePath}/${target}` && !searchParams.get("view");
  };

  useEffect(() => {
    if (!isMobile || !toggled) {
      return;
    }

    requestAnimationFrame(() => {
      document
        .querySelector(".safe-sidebar-mobile .ps-sidebar-container")
        ?.scrollTo({ top: 0, left: 0 });
    });
  }, [isMobile, toggled]);

  if (!user) {
    return null;
  }

  return (
    <Sidebar
      className={isMobile ? "safe-sidebar-mobile" : "safe-sidebar-desktop"}
      collapsed={false}
      toggled={toggled}
      onBackdropClick={() => onToggle?.(false)}
      breakPoint="md"
      customBreakPoint="899.95px"
      width="248px"
      rootStyles={{
        position: isMobile ? "fixed" : "relative",
        top: isMobile ? 0 : undefined,
        left: isMobile ? 0 : undefined,
        bottom: isMobile ? 0 : undefined,
        height: isMobile ? "100dvh" : "100%",
        borderRight: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        zIndex: isMobile ? theme.zIndex.appBar - 1 : theme.zIndex.drawer,
        "& .ps-sidebar-container": {
          height: isMobile ? "100dvh" : "100%",
          paddingTop: isMobile ? "64px" : 0,
          overflowY: "auto",
          backgroundColor: theme.palette.background.paper,
        },
        "& .ps-sidebar-backdrop": {
          top: isMobile ? "64px" : 0,
          height: isMobile ? "calc(100dvh - 64px)" : "100%",
        },
      }}
    >
      <Menu
        closeOnClick={isMobile}
        menuItemStyles={{
          root: {
            fontSize: "0.9rem",
            fontWeight: 600,
          },
          button: ({ active, level }) => ({
            height: level === 0 ? 44 : 38,
            margin: level === 0 ? "4px 10px" : "2px 10px 2px 18px",
            paddingLeft: level === 0 ? 12 : 16,
            paddingRight: 12,
            borderRadius: 8,
            color: active
              ? theme.palette.primary.main
              : theme.palette.text.secondary,
            backgroundColor: active
              ? hexToRGBA(theme.palette.primary.main, 0.12)
              : "transparent",
            [`&.${menuClasses.active}`]: {
              color: theme.palette.primary.main,
              backgroundColor: hexToRGBA(theme.palette.primary.main, 0.12),
              fontWeight: 800,
            },
            "&:hover": {
              color: theme.palette.primary.main,
              backgroundColor: hexToRGBA(theme.palette.primary.main, 0.08),
            },
          }),
          icon: ({ active }) => ({
            minWidth: 34,
            color: active
              ? theme.palette.primary.main
              : theme.palette.text.secondary,
          }),
          label: {
            whiteSpace: "normal",
            lineHeight: 1.25,
          },
          subMenuContent: {
            backgroundColor: theme.palette.background.paper,
          },
          SubMenuExpandIcon: ({ open }) => ({
            color: open
              ? theme.palette.primary.main
              : theme.palette.text.disabled,
          }),
        }}
      >
        {ossNavigationItems.map((item) => (
          <MenuItem
            key={item.label}
            icon={<Icon name={item.icon} width={18} height={18} />}
            active={isActive(item.path)}
            onClick={() => move(item.path)}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </Sidebar>
  );
};
